import React, { useState, useEffect } from "react";
import { Row, Col, Button, Container, Badge } from "react-bootstrap";
import PaginationBar from "../../Components/PaginationBar";
import customloader from "../../assets/images/RollingLoader.gif";
import NoResult from "../../Components/NoResult";
import SearchInput from "../../Components/SearchInput";
import SearchableSelect from "../../Components/SearchableSelect";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faToggleOn, faToggleOff } from "@fortawesome/free-solid-svg-icons";
import { getAlertMessages } from "../../api/getapi";
import { activateAlertMessage, deactivateAlertMessage, deleteAlertMessage } from "../../api/postapi";
import { toast } from "react-toastify";
import moment from "moment";

const severityBadge = (severity) => {
  const map = {
    info: { bg: "primary", text: "Info" },
    warning: { bg: "warning", text: "Warning" },
    error: { bg: "danger", text: "Error" },
  };
  const cfg = map[severity] || map.info;
  return <Badge bg={cfg.bg}>{cfg.text}</Badge>;
};

const AlertMessages = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [noOfPages, setNoOfPages] = useState(1);
  const [sizeOfPages, setSizeOfPages] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [toggle, refresh] = useState(false);

  const searchFilter = (e) => {
    e.preventDefault();
    setSearchInput(e.target.value);
  };

  const handlePageSize = (e) => {
    setSizeOfPages(e.value);
  };

  const changePage = (page) => {
    setCurrentPage(page);
  };

  const fetchAlerts = async () => {
    setIsLoading(true);
    try {
      const res = await getAlertMessages(currentPage, sizeOfPages, searchInput);
      const data = res?.data;
      if (data) {
        setAlerts(data?.data || []);
        setCount(data?.pagination?.no_of_items || 0);
        setNoOfPages(data?.pagination?.no_of_pages || 1);
      }
    } catch (error) {
      setAlerts([]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sizeOfPages, searchInput, toggle]);

  useEffect(() => {
    if (currentPage === 1) {
      refresh((prev) => !prev);
    } else {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleActivate = async (id) => {
    try {
      const res = await activateAlertMessage(id);
      toast.success(res?.data?.message || "Alert activated", {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 1500,
        pauseOnHover: false,
        className: "toastMsg",
      });
      refresh((prev) => !prev);
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Failed to activate", {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 1500,
        pauseOnHover: false,
        className: "toastMsg",
      });
    }
  };

  const handleDeactivate = async (id) => {
    try {
      const res = await deactivateAlertMessage(id);
      toast.success(res?.data?.message || "Alert deactivated", {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 1500,
        pauseOnHover: false,
        className: "toastMsg",
      });
      refresh((prev) => !prev);
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Failed to deactivate", {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 1500,
        pauseOnHover: false,
        className: "toastMsg",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this alert message?")) return;
    try {
      const res = await deleteAlertMessage(id);
      toast.success(res?.data?.message || "Alert deleted", {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 1500,
        pauseOnHover: false,
        className: "toastMsg",
      });
      refresh((prev) => !prev);
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Failed to delete", {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: 1500,
        pauseOnHover: false,
        className: "toastMsg",
      });
    }
  };

  return (
    <div>
      <Container fluid>
        <Row className="mb-3">
          <Col xs="6" lg="3" className="d-none d-lg-inline">
            <SearchInput searchlogo handleChange={searchFilter} value={searchInput} />
          </Col>
          <Col xs="6" lg="4" className="d-none d-lg-inline"></Col>
          <Col xs="6" lg="2">
            <SearchableSelect
              selectClassName={"bg-white"}
              placeholder={`Showing ${sizeOfPages}`}
              defaultValue={`Showing ${sizeOfPages}`}
              value={`Showing ${sizeOfPages}`}
              searchable={false}
              options={[
                { value: "10", label: "10" },
                { value: "20", label: "20" },
                { value: "30", label: "30" },
              ]}
              handleChange={handlePageSize}
            />
          </Col>
          <Col xs="6" lg="3">
            <Link to={"/alert-messages/add"}>
              <Button className="h-100 w-100" style={{ fontSize: "0.75rem" }} size="sm" type="submit">
                <span className="d-none d-lg-inline">Add Alert Message</span> <FontAwesomeIcon icon={faPlus} />
              </Button>
            </Link>
          </Col>
        </Row>

        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
            <img src={customloader} alt="customloader" width="100" height="100" />
          </div>
        ) : !alerts[0] ? (
          <NoResult name="Alert Messages" />
        ) : (
          <>
            <div className="table-responsive bg-white rounded-3 mt-3">
              <table className="table table-striped text-center mb-0" style={{ fontSize: "13px" }}>
                <thead className="text-secondary" style={{ fontWeight: 400 }}>
                  <tr>
                    <th className="py-3">Message</th>
                    <th className="py-3">Severity</th>
                    <th className="py-3">Status</th>
                    <th className="py-3">Link URL</th>
                    <th className="py-3">Bg Color</th>
                    <th className="py-3">Created By</th>
                    <th className="py-3">Created At</th>
                    <th className="py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((row) => (
                    <tr key={row.id} className="border-bottom">
                      <td className="border-bottom-0 text-wrap" style={{ maxWidth: "300px" }}>
                        {row.message?.length > 60 ? `${row.message.substring(0, 60)}...` : row.message}
                      </td>
                      <td className="border-bottom-0">{severityBadge(row.severity)}</td>
                      <td className="border-bottom-0">
                        <Badge bg={row.is_active ? "success" : "secondary"}>
                          {row.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="border-bottom-0" style={{ maxWidth: "150px" }}>
                        {row.link_url ? (
                          <a href={row.link_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "12px" }}>
                            {row.link_url.length > 25 ? `${row.link_url.substring(0, 25)}...` : row.link_url}
                          </a>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="border-bottom-0">
                        {row.bg_color ? (
                          <span className="d-inline-flex align-items-center gap-1">
                            <span style={{ display: "inline-block", width: "14px", height: "14px", borderRadius: "3px", background: row.bg_color, border: "1px solid #ccc" }} />
                            {row.bg_color}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="border-bottom-0">{row.created_by_name || "—"}</td>
                      <td className="border-bottom-0">
                        {row.created_at ? moment(row.created_at).format("DD MMM YYYY, HH:mm") : "—"}
                      </td>
                      <td className="border-bottom-0">
                        <div className="d-flex justify-content-center gap-2">
                          {row.is_fallback ? (
                            <Badge bg="light" text="dark" className="border">
                              Fallback
                            </Badge>
                          ) : (
                            <>
                              {row.is_active ? (
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  style={{ fontSize: "12px", padding: "4px 8px" }}
                                  onClick={() => handleDeactivate(row.id)}
                                >
                                  <FontAwesomeIcon icon={faToggleOff} /> Deactivate
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  style={{ fontSize: "12px", padding: "4px 8px" }}
                                  onClick={() => handleActivate(row.id)}
                                >
                                  <FontAwesomeIcon icon={faToggleOn} /> Activate
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline-primary"
                                style={{ fontSize: "12px", padding: "4px 8px" }}
                                onClick={() => navigate(`/alert-messages/edit/${row.id}`)}
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                style={{ fontSize: "12px", padding: "4px 8px" }}
                                onClick={() => handleDelete(row.id)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Row className="mt-4">
              <Col className="d-none d-lg-block" md="6" lg="3">
                <span className="text-secondary fs-6">{count} results</span>
              </Col>
              <Col xs="12" lg="6">
                <PaginationBar noOfPages={noOfPages} currentPage={currentPage} changePage={changePage} />
              </Col>
              <Col className="d-md-none d-flex align-items-center mt-3" xs="6" lg="3">
                <span className="text-secondary fs-6">{count} Result</span>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default AlertMessages;
